import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTheme } from '../../context/ThemeContext';

// Icons
const CameraIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M14.5 4h-5L7 7H4a2 2 0 0 0-2 2v9a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2h-3l-2.5-3z" />
    <circle cx="12" cy="13" r="3" />
  </svg>
);

const CardsIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="3" width="18" height="18" rx="2" />
    <path d="M3 9h18" />
    <path d="M9 21V9" />
  </svg>
);

const MoveIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M18 8L22 12L18 16" />
    <path d="M2 12H22" />
  </svg>
);

const CloseIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M18 6L6 18" />
    <path d="M6 6l12 12" />
  </svg>
);

const BackIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="m15 18-6-6 6-6" />
  </svg>
);

const CheckIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

const AddToBinderModal = ({
  isOpen,
  onClose,
  binder,
  allCards = [],
  binders = [],
  onAddNewCard,
  onMoveCards,
}) => {
  const { theme, isDark } = useTheme();
  const [view, setView] = useState('options'); // 'options', 'unassigned', 'from-binders'
  const [selectedCards, setSelectedCards] = useState(new Set());
  const [selectedSourceBinder, setSelectedSourceBinder] = useState(null);

  // Get unassigned cards (no binder_id)
  const unassignedCards = allCards.filter((card) => !card.binder_id);

  // Get cards from other binders
  const otherBinders = binders.filter((b) => b.id !== binder?.id);
  const cardsInOtherBinders = allCards.filter(
    (card) => card.binder_id && card.binder_id !== binder?.id
  );

  // Get cards for selected source binder
  const cardsInSourceBinder = selectedSourceBinder
    ? allCards.filter((card) => card.binder_id === selectedSourceBinder.id)
    : [];

  const toggleCardSelection = (cardId) => {
    const newSelected = new Set(selectedCards);
    if (newSelected.has(cardId)) {
      newSelected.delete(cardId);
    } else {
      newSelected.add(cardId);
    }
    setSelectedCards(newSelected);
  };

  const handleConfirmSelection = () => {
    const cardsToMove = allCards.filter((c) => selectedCards.has(c.id));
    onMoveCards?.(cardsToMove, binder);
    resetAndClose();
  };

  const resetAndClose = () => {
    setView('options');
    setSelectedCards(new Set());
    setSelectedSourceBinder(null);
    onClose();
  };

  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      setView('options');
      setSelectedCards(new Set());
      setSelectedSourceBinder(null);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const renderOptionsView = () => (
    <>
      {/* Header */}
      <div style={styles.header}>
        <h2 style={{...styles.title, color: theme.text}}>Add to {binder?.name}</h2>
        <button style={{...styles.closeButton, backgroundColor: theme.bgCard, border: `1px solid ${theme.border}`, color: theme.textSecondary}} onClick={resetAndClose}>
          <CloseIcon />
        </button>
      </div>

      {/* Action Options */}
      <div style={styles.actions}>
        <button
          style={{...styles.actionButton, backgroundColor: theme.bgCard, border: `1px solid ${theme.border}`}}
          onClick={() => {
            onAddNewCard?.(binder);
            resetAndClose();
          }}
        >
          <div style={{...styles.actionIcon, color: theme.accent}}>
            <CameraIcon />
          </div>
          <div style={styles.actionContent}>
            <span style={{...styles.actionTitle, color: theme.text}}>Add New Card</span>
            <span style={{...styles.actionDesc, color: theme.textSecondary}}>Scan or upload a new card</span>
          </div>
        </button>

        <button
          style={{
            ...styles.actionButton,
            backgroundColor: theme.bgCard,
            border: `1px solid ${theme.border}`,
            ...(unassignedCards.length === 0 ? styles.actionButtonDisabled : {}),
          }}
          onClick={() => unassignedCards.length > 0 && setView('unassigned')}
          disabled={unassignedCards.length === 0}
        >
          <div
            style={{
              ...styles.actionIcon,
              color: unassignedCards.length === 0 ? theme.textSecondary : theme.accent,
              ...(unassignedCards.length === 0 ? {backgroundColor: theme.border} : {}),
            }}
          >
            <CardsIcon />
          </div>
          <div style={styles.actionContent}>
            <span
              style={{
                ...styles.actionTitle,
                color: unassignedCards.length === 0 ? theme.textSecondary : theme.text,
              }}
            >
              From Collection
            </span>
            <span style={{...styles.actionDesc, color: theme.textSecondary}}>
              {unassignedCards.length > 0
                ? `${unassignedCards.length} unassigned cards`
                : 'No unassigned cards'}
            </span>
          </div>
        </button>

        <button
          style={{
            ...styles.actionButton,
            backgroundColor: theme.bgCard,
            border: `1px solid ${theme.border}`,
            ...(cardsInOtherBinders.length === 0 ? styles.actionButtonDisabled : {}),
          }}
          onClick={() => cardsInOtherBinders.length > 0 && setView('from-binders')}
          disabled={cardsInOtherBinders.length === 0}
        >
          <div
            style={{
              ...styles.actionIcon,
              color: cardsInOtherBinders.length === 0 ? theme.textSecondary : theme.accent,
              ...(cardsInOtherBinders.length === 0 ? {backgroundColor: theme.border} : {}),
            }}
          >
            <MoveIcon />
          </div>
          <div style={styles.actionContent}>
            <span
              style={{
                ...styles.actionTitle,
                color: cardsInOtherBinders.length === 0 ? theme.textSecondary : theme.text,
              }}
            >
              Move from Binder
            </span>
            <span style={{...styles.actionDesc, color: theme.textSecondary}}>
              {cardsInOtherBinders.length > 0
                ? `${cardsInOtherBinders.length} cards in other binders`
                : 'No cards in other binders'}
            </span>
          </div>
        </button>
      </div>
    </>
  );

  const renderCardGrid = (cards) => (
    <div style={styles.cardGrid}>
      {cards.map((card) => (
        <button
          key={card.id}
          style={{
            ...styles.cardItem,
            backgroundColor: theme.bgCard,
            ...(selectedCards.has(card.id) ? {...styles.cardItemSelected, border: `2px solid ${theme.accent}`} : {}),
          }}
          onClick={() => toggleCardSelection(card.id)}
        >
          <img
            src={card.digital_image_url || card.original_image_url}
            alt={card.name}
            style={styles.cardImage}
          />
          {selectedCards.has(card.id) && (
            <div style={{...styles.cardCheck, backgroundColor: theme.accent, color: '#fff'}}>
              <CheckIcon />
            </div>
          )}
        </button>
      ))}
    </div>
  );

  const renderUnassignedView = () => (
    <>
      {/* Header with back */}
      <div style={styles.header}>
        <button style={{...styles.backButton, backgroundColor: theme.bgCard, border: `1px solid ${theme.border}`, color: theme.text}} onClick={() => setView('options')}>
          <BackIcon />
        </button>
        <h2 style={{...styles.titleCentered, color: theme.text}}>Select Cards</h2>
        <button style={{...styles.closeButton, backgroundColor: theme.bgCard, border: `1px solid ${theme.border}`, color: theme.textSecondary}} onClick={resetAndClose}>
          <CloseIcon />
        </button>
      </div>

      <p style={styles.subtitle}>
        {selectedCards.size} of {unassignedCards.length} selected
      </p>

      {/* Card Grid */}
      <div style={styles.scrollContainer}>{renderCardGrid(unassignedCards)}</div>

      {/* Confirm Button */}
      {selectedCards.size > 0 && (
        <button style={{...styles.confirmButton, backgroundColor: theme.accent, color: '#fff'}} onClick={handleConfirmSelection}>
          Add {selectedCards.size} Card{selectedCards.size !== 1 ? 's' : ''}
        </button>
      )}
    </>
  );

  const renderFromBindersView = () => (
    <>
      {/* Header with back */}
      <div style={styles.header}>
        <button
          style={{...styles.backButton, backgroundColor: theme.bgCard, border: `1px solid ${theme.border}`, color: theme.text}}
          onClick={() => {
            if (selectedSourceBinder) {
              setSelectedSourceBinder(null);
              setSelectedCards(new Set());
            } else {
              setView('options');
            }
          }}
        >
          <BackIcon />
        </button>
        <h2 style={{...styles.titleCentered, color: theme.text}}>
          {selectedSourceBinder ? selectedSourceBinder.name : 'Select Binder'}
        </h2>
        <button style={{...styles.closeButton, backgroundColor: theme.bgCard, border: `1px solid ${theme.border}`, color: theme.textSecondary}} onClick={resetAndClose}>
          <CloseIcon />
        </button>
      </div>

      {!selectedSourceBinder ? (
        // Binder selection
        <div style={styles.binderList}>
          {otherBinders.map((b) => {
            const cardCount = allCards.filter((c) => c.binder_id === b.id).length;
            return (
              <button
                key={b.id}
                style={{...styles.binderOption, backgroundColor: theme.bgCard, border: `1px solid ${theme.border}`}}
                onClick={() => setSelectedSourceBinder(b)}
              >
                <span style={{...styles.binderOptionName, color: theme.text}}>{b.name}</span>
                <span style={{...styles.binderOptionCount, color: theme.textSecondary}}>{cardCount} cards</span>
              </button>
            );
          })}
        </div>
      ) : (
        // Card selection from selected binder
        <>
          <p style={styles.subtitle}>
            {selectedCards.size} of {cardsInSourceBinder.length} selected
          </p>
          <div style={styles.scrollContainer}>{renderCardGrid(cardsInSourceBinder)}</div>
          {selectedCards.size > 0 && (
            <button style={{...styles.confirmButton, backgroundColor: theme.accent, color: '#fff'}} onClick={handleConfirmSelection}>
              Move {selectedCards.size} Card{selectedCards.size !== 1 ? 's' : ''}
            </button>
          )}
        </>
      )}
    </>
  );

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            style={{...styles.backdrop, backgroundColor: theme.overlay}}
            onClick={resetAndClose}
          />

          {/* Modal */}
          <motion.div
            initial={{ opacity: 0, y: 100 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 100 }}
            transition={{ type: 'spring', damping: 25, stiffness: 300 }}
            style={{
              ...styles.modal,
              backgroundColor: theme.bgSheet,
              ...(view !== 'options' ? styles.modalExpanded : {}),
            }}
          >
            {/* Handle */}
            <div style={styles.handle} />

            {view === 'options' && renderOptionsView()}
            {view === 'unassigned' && renderUnassignedView()}
            {view === 'from-binders' && renderFromBindersView()}
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

const styles = {
  backdrop: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 1000,
  },
  modal: {
    position: 'fixed',
    bottom: '80px', // Account for bottom nav
    left: '8px',
    right: '8px',
    borderRadius: '20px',
    padding: '12px 20px 24px',
    zIndex: 1001,
    maxHeight: '45vh',
    display: 'flex',
    flexDirection: 'column',
    boxShadow: '0 -4px 20px rgba(0, 0, 0, 0.5)',
  },
  modalExpanded: {
    maxHeight: 'calc(100vh - 160px)',
  },
  handle: {
    width: '36px',
    height: '4px',
    backgroundColor: '#3a3a3a',
    borderRadius: '2px',
    margin: '0 auto 16px',
    flexShrink: 0,
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: '16px',
    flexShrink: 0,
  },
  title: {
    fontSize: '18px',
    fontWeight: '600',
    margin: 0,
  },
  titleCentered: {
    fontSize: '18px',
    fontWeight: '600',
    margin: 0,
    flex: 1,
    textAlign: 'center',
  },
  closeButton: {
    width: '32px',
    height: '32px',
    borderRadius: '8px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    flexShrink: 0,
  },
  backButton: {
    width: '32px',
    height: '32px',
    borderRadius: '8px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    flexShrink: 0,
  },
  subtitle: {
    color: '#8a8a8a',
    fontSize: '13px',
    margin: '0 0 12px 0',
    textAlign: 'center',
    flexShrink: 0,
  },
  actions: {
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
  },
  actionButton: {
    display: 'flex',
    alignItems: 'center',
    gap: '14px',
    padding: '14px',
    borderRadius: '12px',
    cursor: 'pointer',
    transition: 'background-color 0.15s ease, border-color 0.15s ease',
    outline: '0',
    boxShadow: 'none',
    WebkitTapHighlightColor: 'transparent',
    WebkitAppearance: 'none',
    MozAppearance: 'none',
  },
  actionButtonDisabled: {
    opacity: 0.5,
    cursor: 'not-allowed',
  },
  actionIcon: {
    width: '44px',
    height: '44px',
    borderRadius: '10px',
    backgroundColor: 'rgba(14, 165, 233, 0.15)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  actionContent: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: '2px',
  },
  actionTitle: {
    fontSize: '14px',
    fontWeight: '600',
  },
  actionDesc: {
    fontSize: '12px',
  },
  scrollContainer: {
    flex: 1,
    overflow: 'auto',
    marginBottom: '12px',
  },
  cardGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(4, 1fr)',
    gap: '10px',
  },
  cardItem: {
    position: 'relative',
    aspectRatio: '0.714',
    borderRadius: '8px',
    overflow: 'hidden',
    border: '2px solid transparent',
    cursor: 'pointer',
    padding: 0,
  },
  cardItemSelected: {
  },
  cardImage: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
  },
  cardCheck: {
    position: 'absolute',
    top: '4px',
    right: '4px',
    width: '22px',
    height: '22px',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  binderList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  binderOption: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '14px 16px',
    borderRadius: '10px',
    cursor: 'pointer',
    outline: '0',
    boxShadow: 'none',
    WebkitTapHighlightColor: 'transparent',
    WebkitAppearance: 'none',
    MozAppearance: 'none',
  },
  binderOptionName: {
    fontSize: '14px',
    fontWeight: '500',
  },
  binderOptionCount: {
    fontSize: '13px',
  },
  confirmButton: {
    width: '100%',
    padding: '14px',
    border: 'none',
    borderRadius: '12px',
    fontSize: '15px',
    fontWeight: '600',
    cursor: 'pointer',
    flexShrink: 0,
  },
};

export default AddToBinderModal;
